'use client';
import React, { useState, useEffect } from 'react';
import { Container } from '@eliadmin/core/src/components/ui/container';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import CustomHeading from '../CustomHeading';
import { cn } from '@eliadmin/core/src/utils/style-utils';

interface CardProps {
  title: string;
  text: string;
}

interface CustomSection3Props {
  title: string;
  cardData: CardProps[];
}

const CustomSection3 = ({ title, cardData }: CustomSection3Props) => {
  const [windowWidth, setWindowWidth] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <section className="relative w-full">
      <Container className="relative z-10 flex w-full max-w-none flex-col items-center justify-center gap-10 bg-[#13161B] bg-cover bg-center bg-no-repeat pb-20 md:bg-section-3">
        <Image
          alt="carup"
          width={0}
          height={0}
          sizes="100vw"
          className="h-full w-[145px] pt-10 md:hidden"
          src={'/images/car-up.svg'}
        />
        <CustomHeading
          className="pb-20 pt-10 text-center text-3xl md:pt-20 md:text-5xl"
          heading={title}
        />
        {/* cards wrapper */}
        <div
          className={cn(
            'grid h-full w-full grid-cols-1 place-items-center gap-3 md:grid-cols-2 lg:grid-cols-car-grid lg:gap-10',
            'ea-custom-section-3__cards-wrapper',
          )}
        >
          <div className="order-2 flex w-full max-w-[400px] flex-col gap-3 md:order-1 lg:gap-10 xl:ml-10">
            {/* card */}
            <div className="relative flex min-h-[200px] w-full flex-col gap-3 bg-[#12EAA6] p-5 md:max-h-[200px]">
              <h3 className="text-2xl font-bold tracking-widest text-secondary">
                {cardData[0].title}
              </h3>
              <p className="leading-relaxed">{cardData[0].text}</p>
              <CardDecorative side="right" windowWidth={windowWidth} />
            </div>
            {/* card */}
            <div className="relative flex min-h-[200px] w-full flex-col gap-3 bg-[#18DB9D] p-5 md:max-h-[200px]">
              <h3 className="text-2xl font-bold tracking-widest text-secondary">
                {cardData[1].title}
              </h3>
              <p className="leading-relaxed">{cardData[1].text}</p>
              <CardDecorative side="right" windowWidth={windowWidth} />
            </div>
            {/* card */}
            <div className="relative flex min-h-[200px] w-full flex-col gap-3 bg-[#13CC92] p-5 md:max-h-[250px]">
              <h3 className="text-2xl font-bold tracking-widest text-secondary">
                {cardData[2].title}
              </h3>
              <p className="leading-relaxed">{cardData[2].text}</p>
              <CardDecorative
                side="right"
                lineoffset="min-[1030px]:top-[48%] min-[1081px]:top-[48.2%] min-[1114px]:top-[50%]"
                windowWidth={windowWidth}
              />
            </div>
          </div>
          <Image
            alt="carup"
            width={0}
            height={0}
            sizes="100vw"
            className="relative order-1 hidden h-full w-[250px] md:top-[35%] md:order-2 md:block lg:top-0"
            src={'/images/car-up.svg'}
          />
          <div className="order-3 flex w-full max-w-[400px] flex-col gap-3 lg:gap-10 xl:mr-10">
            <div className="relative flex w-full flex-col gap-3 bg-[#18DB9D] p-5 pl-8 md:max-h-[270px]">
              <h3 className="text-2xl font-bold tracking-widest text-secondary">
                {cardData[3].title}
              </h3>
              <p className="leading-relaxed">{cardData[3].text}</p>
              <CardDecorative
                lineoffset="min-[1024px]:top-[47%] min-[1040px]:top-[48.2%]"
                side="left"
                windowWidth={windowWidth}
              />
            </div>
            <div className="relative flex min-h-[200px] w-full flex-col gap-3 bg-[#13CC92] p-5 pl-8 md:max-h-[250px]">
              <h3 className="text-2xl font-bold tracking-widest text-secondary">
                {cardData[4].title}
              </h3>
              <p className="leading-relaxed">{cardData[4].text}</p>
              <CardDecorative side="left" windowWidth={windowWidth} />
            </div>
          </div>
        </div>
        <div className="absolute inset-0 -z-10 h-full w-full bg-black/80"></div>
      </Container>
      <Image
        src={'/images/fotky/sedem.jpg'}
        alt="Prečo opraviť preliačiny pomocou PDR?"
        width={0}
        height={0}
        className="absolute inset-0 z-0 h-full w-full object-cover"
        sizes="100vw"
      />
    </section>
  );
};

export default CustomSection3;

interface CardDecorativeProps {
  side: 'left' | 'right';
  windowWidth: number;
  lineoffset?: string;
}

const CardDecorative = ({ side, windowWidth, lineoffset }: CardDecorativeProps) => {
  const calculateDecorativeLinePosition = (side: string, width: number) => {
    if (width > 1630) {
      return side === 'right' ? 'right-[-98px]' : 'lg:left-[-98px] md:right-[-98px]';
    } else if (width >= 1530 && width <= 1630) {
      return side === 'right' ? 'right-[-103px]' : 'lg:left-[-103px] md:right-[-103px]';
    } else if (width >= 1430 && width <= 1530) {
      return side === 'right' ? 'right-[-83px]' : 'lg:left-[-83px] md:right-[-83px]';
    } else if (width >= 1330 && width <= 1430) {
      return side === 'right' ? 'right-[-83px]' : 'lg:left-[-83px] md:right-[-83px]';
    } else if (width >= 1230 && width <= 1330) {
      return side === 'right' ? 'right-[-73px]' : 'lg:left-[-73px] md:right-[-73px]';
    } else if (width >= 1130 && width <= 1230) {
      return side === 'right' ? 'right-[-53px]' : 'lg:left-[-53px] md:right-[-53px]';
    } else if (width >= 1030 && width <= 1130) {
      return side === 'right' ? 'right-[-80px]' : 'lg:left-[-80px] md:right-[-80px]';
    } else if (width >= 1024 && width <= 1029) {
      return side === 'right' ? 'right-[-78px]' : 'lg:left-[-78px] md:right-[-78px]';
    } else if (width >= 930 && width <= 1023) {
      return side === 'right' ? 'right-[-138px]' : 'lg:left-[-138px] md:right-[-138px]';
    } else if (width < 930) {
      return side === 'right' ? 'right-[-118px]' : 'lg:left-[-118px] md:right-[-118px]';
    }
  };

  const decorativeLinePosition = calculateDecorativeLinePosition(side, windowWidth);

  return (
    <>
      {/* Outer circle gray */}
      <div
        className={`absolute hidden md:flex ${
          side === 'right'
            ? 'right-[-20px] top-[40%]'
            : 'top-[40%] md:right-[-20px] lg:left-[-20px]'
        } right-[-20px] top-[40%] flex h-10 w-10 items-center justify-center rounded-full bg-[#384459]`}
      >
        {/* Inner circle white */}
        <div className="relative flex h-5 w-5 items-center justify-center rounded-full bg-white ">
          {/* Inner circle gray */}
          <div className="absolute left-1/2 top-1/2 h-[15px] w-[15px] -translate-x-1/2 -translate-y-1/2 transform rounded-full bg-[#384459]"></div>
        </div>
      </div>
      {/* Line */}
      <div
        className={`decorLine absolute hidden border-none md:flex ${decorativeLinePosition} top-[50%] ${lineoffset} h-[3px] bg-white`}
      ></div>
    </>
  );
};
